import { PRIMARY_ASSET, type AssetId } from '@/core/project/sequence';
import type { MediaInfo, SoundInfo } from '@/engine/types';
import { pickVideoFiles, VIDEO_ACCEPT } from '@/lib/video-files';
import type { AssetLookup, AssetShape } from './selectors';

/**
 * Everything brought into one editing session: the opened video and whatever
 * else was imported (more videos, pictures, sounds). Files never leave the
 * device; the editor only holds references and object URLs.
 */
export type AssetKind = 'video' | 'image' | 'audio';

export interface Asset {
  readonly id: AssetId;
  readonly kind: AssetKind;
  readonly file: File;
  /** Object URL for previewing it. */
  readonly url: string;
  /** Picture size (a sound: 0). */
  readonly width: number;
  readonly height: number;
  /** Seconds (a picture: 0). */
  readonly durationSec: number;
  /** Details of a video. */
  readonly video: MediaInfo | null;
  /** Details of a sound file. */
  readonly sound: SoundInfo | null;
  /** Whether it has sound this browser can play. */
  readonly hasAudio: boolean;
  /** Taken out of the media list; kept so an undo can still bring back where it was used. */
  readonly removed: boolean;
}

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif'];
const IMAGE_EXTENSION = /\.(png|jpe?g|webp|gif|avif)$/i;
const AUDIO_EXTENSION = /\.(mp3|m4a|aac|wav|wave|flac|ogg|oga|opus)$/i;

/**
 * File input `accept` for everything the media list takes. SVG is left out on
 * purpose: export decodes pictures in a worker, which can't draw SVG.
 */
export const MEDIA_ACCEPT = `${VIDEO_ACCEPT},${IMAGE_TYPES.join(',')},audio/*,.mp3,.m4a,.aac,.wav,.flac,.ogg,.opus`;

/** What kind of media a file is, by its type or name; null for anything else. */
export function assetKindOf(file: File): AssetKind | null {
  if (pickVideoFiles([file]).length > 0) return 'video';
  if (IMAGE_TYPES.includes(file.type) || IMAGE_EXTENSION.test(file.name)) return 'image';
  if (file.type.startsWith('audio/') || AUDIO_EXTENSION.test(file.name)) return 'audio';
  return null;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Files in the order cameras and phones name them (IMG_9 before IMG_10). */
export const byName = (files: readonly File[]): File[] => [...files].sort((a, b) => collator.compare(a.name, b.name));

let counter = 0;
/** A fresh id for an imported asset (the opened video is always {@link PRIMARY_ASSET}). */
export const nextAssetId = (): AssetId => `a${++counter}`;
/** A fresh id for a clip or a layer. */
export const nextItemId = (prefix: 'clip' | 'layer'): string => `${prefix}-${Date.now().toString(36)}-${++counter}`;

export const isPrimary = (asset: Pick<Asset, 'id'>): boolean => asset.id === PRIMARY_ASSET;

export const assetShape = (asset: Asset): AssetShape => ({ kind: asset.kind, durationSec: asset.durationSec, width: asset.width, height: asset.height });

/** Lookup the selectors use; the opened video is passed to them separately. */
export const assetLookup = (assets: Readonly<Record<AssetId, Asset>>): AssetLookup => (id) => {
  const asset = assets[id];
  return asset && assetShape(asset);
};

/** Natural size of a picture, decoded on this device; rejects for a file the browser can't show. */
export async function readImageSize(file: Blob): Promise<{ readonly width: number; readonly height: number }> {
  const bitmap = await createImageBitmap(file);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}

/** A display name without the extension, for lists and the timeline. */
export const displayName = (file: Pick<File, 'name'>): string => file.name.replace(/\.[^.]+$/, '') || file.name;
